import React, { memo, useState, useEffect, useMemo } from "react";
import { NewValueParams } from "ag-grid-community";
import { Grid, AgGridReact, AgGrid, Flex } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { LoadingPage } from "~/components/LoadingPage/LoadingPage";
import { useGetCurrentUserQuery } from "~/graphql/mainHooks";
import { useGetProgramsQuery, useUserScopedArenaIdsQuery } from "~/graphql/supergraphHooks";
import { EnableComplianceCellRenderer } from "~/pages/Compliance/AssignProgramTable/EnableComplianceCellRenderer";
import EnableComplianceWarningModal from "~/pages/Compliance/AssignProgramTable/EnableComplianceWarningModal";
import { LegacyKycCellRenderer } from "~/pages/Compliance/AssignProgramTable/LegacyKycCellRenderer";
import { LoadingCellRenderer } from "~/pages/Compliance/Common/LoadingCellRenderer";
import { AGGridCellRenderParams } from "~/pages/Compliance/ComplianceTable/types";
import { isComplianceManager } from "~/utils/userRoles";
import { useEnableClientLevelPermissions } from "../flags";
import { dashboardContainer } from "../styles_vanilla.css";
import AssignProgramWarningModal from "./AssignProgramWarningModal";
import { InvestmentEntitiesGraphqlDataSource } from "./investmentEntitiesGraphqlDataSource";

interface AssignProgramTableProps {
  setGridApi?: (gridApi: AgGrid.GridApi | null) => void;
  setDataSource?: (dataSource: InvestmentEntitiesGraphqlDataSource | null) => void;
}

export const AssignProgramTable = memo(
  ({
    setGridApi: setExternalGridApi,
    setDataSource: setExternalDataSource,
  }: AssignProgramTableProps = {}) => {
    const [showModal, setShowModal] = useState(false);
    const [showEnabledModal, setShowEnabledModal] = useState(false);
    const [oldValue, setOldValue] = useState("");
    const [newValue, setNewValue] = useState("");
    const [newEnabledValue, setNewEnabledValue] = useState("");
    const [entityName, setEntityName] = useState("");
    const [programName, setProgramName] = useState("");
    const [entityId, setEntityId] = useState("");
    const [arenaId, setArenaId] = useState("");

    const { data } = useGetCurrentUserQuery({
      context: withClientContext("staff"),
      notifyOnNetworkStatusChange: true,
    });
    const roles = data?.currentUser?.roles?.edges
      .map((edge) => edge.node.name)
      .filter((node) => !!node) as string[];

    const programsResult = useGetProgramsQuery();
    const clientLevelPermissionsEnabled = useEnableClientLevelPermissions();
    const userScopedArenaIdsResult = useUserScopedArenaIdsQuery({
      skip: !clientLevelPermissionsEnabled,
    });

    const investmentEntitiesGraphqlDataSource = useMemo(
      () =>
        new InvestmentEntitiesGraphqlDataSource(
          programsResult.data?.programsAssignableToEntities,
          userScopedArenaIdsResult.data?.userScopedArenaIds ?? null,
        ),
      [
        programsResult.data?.programsAssignableToEntities,
        userScopedArenaIdsResult.data?.userScopedArenaIds,
      ],
    );

    useEffect(() => {
      setExternalDataSource?.(investmentEntitiesGraphqlDataSource);
    }, [investmentEntitiesGraphqlDataSource, setExternalDataSource]);

    if (userScopedArenaIdsResult.loading) {
      return <LoadingPage />;
    }

    const programNames = programsResult.data?.programsAssignableToEntities?.map(
      (program) => program.name,
    ) as string[];
    const programsAndEmpty = [...["--"], ...(programNames || [])];

    const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
      autoSizeStrategy: {
        type: "fitGridWidth",
      },
      stopEditingWhenCellsLoseFocus: true,
      statusBar: {
        statusPanels: [
          { statusPanel: "agTotalRowCountComponent" },
          { statusPanel: "agSelectedRowCountComponent" },
        ],
      },
      rowModelType: "serverSide",
      serverSideDatasource: investmentEntitiesGraphqlDataSource,
      getRowId: (params) => params.data.caseId?.toString(),
    };
    // Function to handle value change
    const handleValueChange = (params: NewValueParams<any, any>) => {
      if (params.node) {
        // Ensure params.node is not null
        setNewValue(params.newValue);
        params.oldValue === "" ? setOldValue("--") : setOldValue(params.oldValue);
        setEntityName(params.node.data.name);
        setEntityId(params.node.data.id);
        setArenaId(params.node.data.arenaId);
        params.api.refreshCells({
          rowNodes: [params.node], // Refresh the affected row
          columns: ["details"], // Refresh the details column to show the new component
        });
        setShowModal(true);
      }
    };
    const handleEnabledValueChange = (params: NewValueParams<any, any>) => {
      if (params.node) {
        // Ensure params.node is not null
        setNewEnabledValue(params.newValue);
        setEntityName(params.node.data.name);
        setEntityId(params.node.data.id);
        setArenaId(params.node.data.arenaId);
        setProgramName(params.node.data.complianceProgram);
        params.api.refreshCells({
          rowNodes: [params.node], // Refresh the affected row
          columns: ["details"], // Refresh the details column to show the new component
        });
        setShowEnabledModal(true);
      }
    };
    const onClose = () => {
      setShowModal(false);
      setNewValue("");
      setOldValue("");
      setEntityName("");
      setEntityId("");
      setArenaId("");
    };

    const onEnabledClose = () => {
      setShowEnabledModal(false);
      setNewValue("");
      setOldValue("");
      setEntityName("");
      setEntityId("");
      setArenaId("");
    };

    const onGridReady = (params: AgGrid.GridReadyEvent) => {
      setExternalGridApi?.(params.api);
    };

    const colDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
      {
        field: "name",
        headerName: "Entity",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          // BE doesn't support multiple AND conditions for the same field, so disable it for now
          maxNumConditions: 1,
        },
      },
      {
        field: "arenaName",
        headerName: "Client",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          // BE doesn't support multiple AND conditions for the same field, so disable it for now
          maxNumConditions: 1,
        },
      },
      {
        field: "isInvestorDiligenceEnabled",
        headerName: "Legacy KYC Enabled",
        cellRenderer: (params: AGGridCellRenderParams) => (
          <LegacyKycCellRenderer params={params} />
        ),
        sortable: false,
      },
      {
        headerName: "Compliance program",
        field: "complianceProgram",
        cellEditor: "agSelectCellEditor",
        editable: isComplianceManager(roles ?? []),
        cellEditorParams: {
          values: programsAndEmpty,
        },
        filter: "agSetColumnFilter",
        filterParams: {
          values: programsAndEmpty,
        },
        onCellValueChanged: (params: NewValueParams) => {
          // Call a function to handle component rendering
          handleValueChange(params);
        },
      },
      {
        headerName: "Compliance program enabled",
        field: "complianceEnabled",
        cellRenderer: (params: AGGridCellRenderParams) => (
          <EnableComplianceCellRenderer params={params} />
        ),
        cellEditor: "agSelectCellEditor",
        editable: function (params) {
          if (!isComplianceManager(roles ?? [])) return false;
          // allow `min_value` cell to be edited for rows with correct `validation_type`
          return params.node.data.complianceProgram !== "--";
        },
        cellEditorParams: {
          values: ["Disabled", "Enabled"],
        },
        onCellValueChanged: (params: NewValueParams) => {
          // Call a function to handle component rendering
          handleEnabledValueChange(params);
        },
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["Disabled", "Enabled", "No compliance program assigned"],
        },
      },
    ];

    const getRowId = (params: AgGrid.GetRowIdParams) => String(params.data.id);
    return (
      <Flex className={dashboardContainer}>
        <Grid
          getRowId={getRowId}
          gridOptions={gridOptions}
          columnDefs={colDefs}
          suppressColumnVirtualisation={true}
          suppressRowVirtualisation={true}
          suppressRowTransform={true}
          // Paging
          pagination
          paginationPageSizeSelector={false}
          paginationPageSize={100}
          loadingCellRenderer={LoadingCellRenderer}
          // SSRM
          rowModelType="serverSide"
          serverSideDatasource={investmentEntitiesGraphqlDataSource}
          cacheBlockSize={200}
          onGridReady={onGridReady}
        />
        {
          <AssignProgramWarningModal
            allPrograms={programsResult.data?.programsAssignableToEntities}
            showModal={showModal}
            arenaId={arenaId}
            entityId={entityId}
            entityName={entityName}
            oldValue={oldValue}
            newValue={newValue}
            onClose={onClose}
          />
        }
        {
          <EnableComplianceWarningModal
            allPrograms={programsResult.data?.programsAssignableToEntities}
            showModal={showEnabledModal}
            arenaId={arenaId}
            entityId={entityId}
            newValue={newEnabledValue}
            entityName={entityName}
            entityProgram={programName}
            onClose={onEnabledClose}
          />
        }
      </Flex>
    );
  },
);
AssignProgramTable.displayName = "AssignProgramTable";
